import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, dialogs, Icon, Input, Link, Text } from '@nvision/ui';

import { Card } from '@/components/card';
import { Page } from '@/components/page';
import { useCompanyUsers } from '@/modules/entities';
import type { RootState } from '@/redux/RootReducer';
import { formatDate } from '@/utils/date';

import { useCreateContractPricing, useDeleteContract, useSuspenseContract, useUpdateContract } from '../../hooks';
import type { NewContract } from '../../models';
import { ChangeStatusDialog } from './change-status-dialog';
import { ContractDetails, type ContractFormRef } from './contract-details';
import { ContractDocuments } from './contract-documents';
import { ContractHistory } from './contract-history';
import { ContractPricing, type ContractPricingRef } from './contract-pricing';

import styles from './contract.module.css';

const DATE_FORMAT = 'MM/dd/yyyy';
const EMPTY_VALUE = '—';

export const Contract: React.FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const { t } = useTranslation(['translation']);
  const navigate = useNavigate();
  const [isChangeStatusOpen, setIsChangeStatusOpen] = useState(false);

  const { data: contract } = useSuspenseContract(contractId ?? '');

  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany, shallowEqual);
  const { data: companyUsers } = useCompanyUsers({
    companyId: String(selectedCompany?.companyId ?? ''),
    searchParams: {
      ids: [contract.createdBy, contract.modifiedBy].filter((id) => id !== null && id !== undefined).map(String),
    },
  });

  const infoFields = [
    { label: 'Created By', value: companyUsers.map[String(contract.createdBy)]?.fullName ?? EMPTY_VALUE },
    { label: 'Created On', value: formatDate(contract.createdOn ?? '', DATE_FORMAT) || EMPTY_VALUE },
    { label: 'Modified By', value: companyUsers.map[String(contract.modifiedBy)]?.fullName ?? EMPTY_VALUE },
    { label: 'Modified On', value: formatDate(contract.modifiedOn ?? '', DATE_FORMAT) || EMPTY_VALUE },
  ];

  const formRef = useRef<ContractFormRef>({} as ContractFormRef);
  const pricingRef = useRef<ContractPricingRef>({} as ContractPricingRef);

  const { mutate: deleteContract } = useDeleteContract({
    onSuccess: () => navigate(-1),
  });

  const { isPending: isLinkingPricing, mutate: createContractPricing } = useCreateContractPricing();

  const { isPending: isUpdating, mutate: updateContract } = useUpdateContract({
    onSuccess: ({ key }) => {
      const { linkedPricing } = pricingRef.current;

      if (linkedPricing.length) {
        createContractPricing(
          { contractKey: key, pricing: linkedPricing },
          { onSuccess: () => pricingRef.current.reset() },
        );
      }
    },
  });

  const handleDeleteContract = () => {
    dialogs.error({
      actions: [
        {
          label: 'Delete contract',
          onPress: () => {
            if (contractId) {
              deleteContract(contractId);
            }
          },
          variant: 'destructive',
        },
        {
          label: t('translation:default.buttons.cancel'),
        },
      ],
      content: 'Are you sure you want to delete the following contract? This action cannot be undone.',
      title: 'Delete contract',
    });
  };

  const close = () => {
    formRef.current?.reset(contract);
    navigate(-1);
  };

  const onClose = () => {
    if (!formRef.current.isDirty() && !pricingRef.current.linkedPricing.length) {
      close();
      return;
    }

    dialogs.warning({
      actions: [
        {
          label: t('translation:default.buttons.close'),
          onPress: close,
          variant: 'destructive',
        },
        {
          label: t('translation:default.buttons.stay'),
        },
      ],
      content: t('translation:default.fields.unsavedChanges'),
      title: t('translation:default.buttons.warning'),
    });
  };

  const onSubmit = (values: NewContract) => {
    updateContract({ ...contract, ...values });
  };

  return (
    <Page>
      <Page.Main className={styles.main}>
        <div className={styles.topBar}>
          <Link className={styles.link} iconLeft={<Icon name="arrow-left" size={20} />} onClick={onClose}>
            Back to previous page
          </Link>
          <div className={styles.info}>
            {infoFields.map(({ label, value }) => (
              <div className={styles.infoField} key={label}>
                <Text className={styles.infoLabel} size="xs" elementType="div">
                  {label}
                </Text>
                <Text className={styles.infoValue} size="sm" elementType="div" weight="semibold">
                  {value}
                </Text>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.container}>
          <Card>
            <Card.Header
              title="Contract Information"
              actions={
                <>
                  <Input
                    value={contract.status ?? ''}
                    placeholder="Contract Status"
                    isDisabled
                    className={styles.statusField}
                  />
                  <Button secondary outline onClick={() => setIsChangeStatusOpen(true)}>
                    Change Status
                  </Button>
                  <div className={styles.divider} />
                  <Button destructive outline onClick={handleDeleteContract}>
                    Delete contract
                  </Button>
                  <div className={styles.divider} />
                  <Button secondary outline onClick={onClose}>
                    {t('translation:default.buttons.cancel')}
                  </Button>
                  <Button primary isLoading={isUpdating || isLinkingPricing} onClick={() => formRef.current?.submit()}>
                    {t('translation:default.buttons.save')}
                  </Button>
                </>
              }
            />
            <Card.Main>
              <ContractDetails formRef={formRef} initialValues={contract} onSubmit={onSubmit} />
              <ContractPricing contractKey={contractId} pricingRef={pricingRef} />
              <ContractDocuments contractKey={contractId} />
            </Card.Main>
          </Card>
          <ContractHistory contractKey={contractId} />
        </div>
        <ChangeStatusDialog
          contractKey={contractId}
          currentStatus={contract.status}
          isOpen={isChangeStatusOpen}
          onClose={() => setIsChangeStatusOpen(false)}
        />
      </Page.Main>
    </Page>
  );
};
